import { Injectable, signal } from '@angular/core';

export interface LoginResponse
{
    Username: string;
    IsAdmin: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService
{
    // Simple signal to store login state
    private user = signal<LoginResponse | null>(null);
    private loginCheckInFlight: Promise<boolean> | null = null;

    async login(email: string, password: string): Promise<void>
    {
        try
        {
            const res = await fetch('/api/authentication/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (!res.ok)
            {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || 'Login failed');
            }

            const data: LoginResponse = await res.json();
            this.user.set(data); // store user info
        } catch (err: any)
        {
            this.user.set(null);
            throw err;
        }
    }

    /** sync read – safe for templates */
    get isLoggedIn(): boolean
    {
        return this.user() !== null;
    }

    get currentUser(): LoginResponse | null
    {
        return this.user();
    }

    async checkLoginAsync(): Promise<boolean>
    {
        // already known
        if (this.user() !== null) return true;

        // already checking
        if (this.loginCheckInFlight)
        {
            return this.loginCheckInFlight;
        }

        this.loginCheckInFlight = (async () =>
        {
            try
            {
                const res = await fetch('/api/authentication/check', {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (!res.ok)
                {
                    this.user.set(null);
                    return false;
                }

                const data: LoginResponse = await res.json();
                this.user.set(data);
                return true;
            }
            catch
            {
                this.user.set(null);
                return false;
            }
            finally
            {
                this.loginCheckInFlight = null;
            }
        })();

        return this.loginCheckInFlight;
    }

    async logoutAsync()
    {
        try
        {
            await fetch('/api/authentication/logoutapi',
                {
                    method: 'POST',
                    credentials: 'include'
                });
        }
        finally
        {
            this.user.set(null);
        }
    }
}
